/**
 * Sprint TC-B.1 — Candidate-facing Recruitment Context panel.
 *
 * Rendered ABOVE the existing assessment flow (`EndUserAssessment.jsx`) so the
 * candidate understands who invited them and what role they are applying for
 * BEFORE they answer the first question. Purely additive — fetches a tiny
 * read-only context endpoint and renders nothing on 404 (legacy assignments
 * without a recruitment process still work unchanged).
 */
import i18n from '@/i18n';
import { bizDate } from '@/lib/dates';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Building2, Briefcase, MapPin, Banknote, CalendarClock, ListChecks,
  ClipboardList, Clock, ChevronDown, ChevronUp, Users,
} from 'lucide-react';
import { api } from '@/lib/api';

const STATUS_TONE = {
  invited: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  submitted: 'bg-green-100 text-green-700 border-green-200',
  closed: 'bg-muted text-muted-foreground border-muted',
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return bizDate(iso, i18n.language); } catch { return iso; }
};

/**
 * @param {{ assignmentId: string }} props
 */
export function RecruitmentContextPanel({ assignmentId }) {
  const { t } = useTranslation();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!assignmentId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/me/assignments/${assignmentId}/recruitment-context`);
        if (alive) setCtx(r.data);
      } catch (e) {
        // 404 = assignment not linked to a recruitment process. Render nothing.
        if (alive) setCtx(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [assignmentId]);

  if (loading || !ctx) return null;

  const statusLabel = t(`recruitment.candidate.status.${ctx.application_status}`, {
    defaultValue: ctx.application_status.replace(/_/g, ' '),
  });
  const tone = STATUS_TONE[ctx.application_status] || STATUS_TONE.invited;

  const closing = fmtDate(ctx.closing_date);

  return (
    <Card
      className="p-5 mb-6 border-primary/20 shadow-sm fade-in-up"
      data-testid="recruitment-context-panel"
    >
      {/* ── Header row ────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary font-semibold">
            <Briefcase className="h-3 w-3" />
            {t('recruitment.candidate.youAreApplying', {
              defaultValue: 'You are being evaluated for',
            })}
          </div>
          <h2 className="text-xl font-semibold mt-0.5 truncate" data-testid="rc-position-title">
            {ctx.position_title || '—'}
          </h2>
          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
            <Building2 className="h-3.5 w-3.5" />
            <span className="truncate" data-testid="rc-company-name">{ctx.company_name || '—'}</span>
            {ctx.department && (
              <>
                <span className="text-muted-foreground/60">·</span>
                <span className="truncate">{ctx.department}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Badge className={`${tone} border`} data-testid="rc-status-badge">
            {statusLabel}
          </Badge>
          {ctx.assessments_total > 0 && (
            <span className="text-xs text-muted-foreground">
              {t('recruitment.candidate.assessmentsCount', {
                defaultValue: '{{done}} of {{total}} assessments',
                done: ctx.assessments_completed,
                total: ctx.assessments_total,
              })}
            </span>
          )}
        </div>
      </div>

      {/* ── Key facts strip ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
        {ctx.work_modality && (
          <Fact icon={MapPin} label={t('recruitment.candidate.modality', { defaultValue: 'Modality' })} value={ctx.work_modality} testId="rc-modality" />
        )}
        {ctx.salary_range && (
          <Fact icon={Banknote} label={t('recruitment.candidate.salaryRange', { defaultValue: 'Salary range' })} value={ctx.salary_range} testId="rc-salary" />
        )}
        {ctx.number_of_openings != null && (
          <Fact icon={Users} label={t('recruitment.candidate.openings', { defaultValue: 'Openings' })} value={String(ctx.number_of_openings)} testId="rc-openings" />
        )}
        {closing && (
          <Fact icon={CalendarClock} label={t('recruitment.candidate.closing', { defaultValue: 'Closing date' })} value={closing} testId="rc-closing" />
        )}
        {ctx.estimated_minutes != null && (
          <Fact icon={Clock} label={t('recruitment.candidate.estimatedTime', { defaultValue: 'Estimated time' })} value={`${ctx.estimated_minutes} min`} testId="rc-est-time" />
        )}
        {ctx.assessments_total > 1 && (
          <Fact icon={ListChecks} label={t('recruitment.candidate.totalAssessments', { defaultValue: 'Total assessments' })} value={String(ctx.assessments_total)} testId="rc-total-assessments" />
        )}
      </div>

      {/* ── Average progress bar (only when there is more than one assessment) ── */}
      {ctx.assessments_total > 1 && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">
              {t('recruitment.candidate.overallProgress', { defaultValue: 'Overall progress' })}
            </span>
            <span className="tabular-nums font-medium">{ctx.average_progress}%</span>
          </div>
          <Progress value={ctx.average_progress} className="h-1.5" />
        </div>
      )}

      {/* ── Collapsible: job description + instructions ───────────── */}
      {(ctx.job_description || ctx.instructions) && (
        <>
          <button
            type="button"
            className="mt-4 flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            onClick={() => setExpanded((v) => !v)}
            data-testid="rc-toggle-details"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded
              ? t('recruitment.candidate.hideDetails', { defaultValue: 'Hide role details' })
              : t('recruitment.candidate.showDetails', { defaultValue: 'Show role details' })}
          </button>
          {expanded && (
            <div className="mt-3 space-y-3 text-sm">
              {ctx.job_description && (
                <div data-testid="rc-jd">
                  <p className="font-medium text-foreground mb-0.5">
                    {t('recruitment.candidate.jobDescription', { defaultValue: 'About the role' })}
                  </p>
                  <p className="text-muted-foreground whitespace-pre-line">
                    {ctx.job_description}
                  </p>
                </div>
              )}
              {ctx.instructions && (
                <div data-testid="rc-instructions">
                  <p className="font-medium text-foreground mb-0.5 flex items-center gap-1.5">
                    <ClipboardList className="h-3.5 w-3.5" />
                    {t('recruitment.candidate.beforeYouStart', { defaultValue: 'Before you start' })}
                  </p>
                  <p className="text-muted-foreground whitespace-pre-line">
                    {ctx.instructions}
                  </p>
                </div>
              )}
              {(ctx.languages_required || []).length > 0 && (
                <div>
                  <p className="font-medium text-foreground mb-0.5">
                    {t('recruitment.candidate.languages', { defaultValue: 'Languages' })}
                  </p>
                  <p className="text-muted-foreground capitalize">
                    {ctx.languages_required.join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function Fact({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-2" data-testid={testId}>
      <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="font-medium truncate">{value}</div>
      </div>
    </div>
  );
}

export default RecruitmentContextPanel;
